import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Link } from "react-router-dom";
import { BlocksRenderer } from "../components/BlocksRenderer";
import { Lightbox } from "../components/Lightbox";
import { usePaginaProyecto } from "../hooks/useCms";
import { mediaUrl } from "../lib/strapi";

/**
 * Página /proyecto: descripción editorial del Campus EMJC.
 *
 * El contenido viene del single type `pagina-proyecto` del CMS:
 *   - titulo + eslogan: cabecera
 *   - cuerpo (bloques): texto explicativo principal
 *   - galeriaImagenes: rejilla visual al final con lightbox accesible
 *
 * Mientras el CMS no tenga la entrada publicada, la página cae a los
 * 3 párrafos hardcodeados (fallback histórico). El equipo puede sustituirlos
 * en cualquier momento desde el admin sin tocar código.
 */

const FALLBACK_TITULO = "El proyecto";
const FALLBACK_ESLOGAN = "Una escuela donde los niños se convierten en científicos cada día.";
const FALLBACK_PARRAFOS = [
  <p key="p1">
    La <strong>Escuela Municipal de Jóvenes Científicos</strong> es una iniciativa conjunta de la
    Universidad de Extremadura, la Fundación Universidad-Sociedad y ayuntamientos extremeños. Hacemos
    divulgación científica y fomentamos vocaciones STEM en niños y niñas de 8 a 12 años.
  </p>,
  <p key="p2">
    Trabajamos con profesores universitarios, investigadores e ingenieros que comparten su entusiasmo
    por la ciencia con los más jóvenes a través de talleres prácticos, campamentos y ferias.
  </p>,
  <p key="p3">
    Nuestro programa educativo desarrolla la creatividad, el pensamiento lógico y la capacidad de
    resolver problemas, conectando matemáticas, física, química, biología, música, pintura, historia
    y arquitectura.
  </p>,
];

export function ProyectoPage() {
  const { data: pagina } = usePaginaProyecto();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const titulo = pagina?.titulo ?? FALLBACK_TITULO;
  const eslogan = pagina?.eslogan ?? FALLBACK_ESLOGAN;
  const cuerpo = pagina?.cuerpo;
  const fotos = pagina?.galeriaImagenes ?? [];

  const seoTitle = pagina?.seoTitle ?? `${titulo} — Campus EMJC`;
  const seoDescription =
    pagina?.seoDescription ??
    "Conoce el proyecto Campus EMJC: divulgación científica para niños de 8 a 12 años en Extremadura, en colaboración con la Universidad de Extremadura.";

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
      </Helmet>

      <section className="container-emjc py-20">
        <p className="text-sm font-semibold uppercase tracking-widest text-brand-orange-deep">
          {titulo}
        </p>
        <h1 className="mt-2 max-w-3xl font-display text-5xl font-semibold leading-tight">
          {eslogan}
        </h1>

        <div className="mt-10 max-w-prose space-y-5 text-lg leading-relaxed text-gray-700">
          {cuerpo && cuerpo.length > 0 ? (
            <div className="prose-emjc">
              <BlocksRenderer blocks={cuerpo} />
            </div>
          ) : (
            <div className="prose-emjc space-y-5">{FALLBACK_PARRAFOS}</div>
          )}

          <p className="not-prose pt-2">
            <Link
              to="/proyecto/equipo"
              className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-5 py-2 text-sm font-semibold text-brand-paper transition hover:bg-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2"
            >
              Conoce al equipo →
            </Link>
          </p>
        </div>
      </section>

      {/* Galería visual al final del texto. Sólo se renderiza si el admin
          ha subido al menos una imagen al campo `galeriaImagenes`. */}
      {fotos.length > 0 && (
        <section className="container-emjc pb-24" aria-labelledby="proyecto-galeria-heading">
          <h2
            id="proyecto-galeria-heading"
            className="font-display text-3xl font-semibold md:text-4xl"
          >
            El proyecto en imágenes
          </h2>
          <ul className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {fotos.map((foto, idx) => {
              const thumbSrc =
                mediaUrl(foto.formats?.medium?.url) ??
                mediaUrl(foto.formats?.small?.url) ??
                mediaUrl(foto.url);
              return (
                <li key={foto.documentId ?? foto.id}>
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(idx)}
                    aria-label={
                      foto.alternativeText
                        ? `Abrir imagen: ${foto.alternativeText}`
                        : `Abrir imagen ${idx + 1} de ${fotos.length}`
                    }
                    className="group block aspect-[4/3] w-full overflow-hidden rounded-lg bg-brand-paper ring-1 ring-gray-300 transition hover:ring-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2"
                  >
                    <img
                      src={thumbSrc}
                      alt={foto.alternativeText ?? ""}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {lightboxIndex !== null && fotos.length > 0 && (
        <Lightbox
          fotos={fotos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </>
  );
}
